<template>
    <div class="post" v-if="post">
        <div v-if="post.image" class="post__image">
            <img :src="post.image.url" :title="post.title" />
        </div>
        <div v-bind:class="{ post__container: true, 'post__container--has-image': post.image || false }">
            <header class="post__header">
                <img v-if="post.author && post.author.image" :src="post.author.image.url" :title="post.author.name" class="post__author__image" />
                <h1 class="post__heading">{{ post.title }}</h1>
                <div class="post__lead" v-html="post.lead"></div>
            </header>
            <div class="post__meta">
                <div class="post__meta__container">
                    <div v-if="post.author" class="post__meta__author">
                        <img v-if="post.author && post.author.image" :src="post.author.image.url" :title="post.author.name" class="post__meta__author__image" />
                        <label>Forfatter:</label>
                        <span>{{ post.author.name }}</span>
                        <p v-if="post.author.organisation">{{ post.author.organisation }}</p>
                    </div>
                    <div class="post__meta__pubdate">
                        <label>Publisert:</label>
                        <span>{{ post.postDate }}</span>
                    </div>
                </div>
                <div v-if="post.category" v-bind:class="cssPostCategoryClass(post)">
                    {{ post.category.name }}
                </div>
            </div>
            <div v-if="post.blocks" class="post__content">
                <section v-for="(block, index) in post.blocks" v-bind:key="index" v-bind:class="cssPostBlockClass(block)">
                    <div v-if="block.typeHandle == 'text'" v-html="block.text"></div>
                    <h2 v-else-if="block.typeHandle == 'heading'" v-html="block.heading"></h2>
                    <figure v-else-if="block.typeHandle == 'image' && block.image">
                        <img :src="block.image.url" />
                        <figcaption v-if="block.caption" v-html="block.caption"></figcaption>
                    </figure>
                    <div v-else-if="block.typeHandle == 'video' && block.videoLink && youtubeId(block.videoLink)" class="post__section__video__container">
                        <iframe
                            width="640"
                            height="360"
                            :src="'https://www.youtube.com/embed/' + youtubeId(block.videoLink)"
                            frameborder="0"
                            allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
                            allowfullscreen
                        >
                        </iframe>
                    </div>                        
                    <div v-else-if="block.typeHandle == 'embed' && block.embed" v-html="block.embed"></div>
                </section>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
    name: 'Post',
    computed: mapState(['site', 'post']),
    metaInfo() {
        const title = this.site.metaTitle + (this.post ? ' - ' + (this.post.metaTitle ? this.post.metaTitle : this.post.title) : '');
        const description = this.post
            ? this.post.metaDescription
                ? this.post.metaDescription
                : this.post.lead.replace(/(<([^>]+)>)/g, '')
            : this.site.metaDescription;
        const ogImage =
            this.post && this.post.ogImage
                ? this.post.ogImage.url
                : this.post && this.post.image
                ? this.post.image.url
                : this.site.ogImage
                ? this.site.ogImage
                : null;
        return {
            title: title,
            meta: [
                { name: 'description', content: description },
                { property: 'og:title', title },
                { property: 'og:site_name', content: this.site.metaTitle },
                { property: 'og:description', content: description },
                { property: 'og:type', content: 'article' },
                { property: 'og:url', content: window.location.href },
                { property: 'og:image', content: ogImage },
            ],
        };
    },
    async created() {
        await this.loadPosts('blogg/' + this.$route.params.slug);
        if (!this.post) {
            this.$router.push({ path: '/404' });
        }
    },
    methods: {
        ...mapActions(['loadPosts']),
        youtubeId(videoLink) {
            const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
            const match = videoLink.match(regExp);
            if (match && match[7].length == 11) {
                return match[7];
            }
        },
        cssPostCategoryClass(post) {
            const cssClass = {
                post__category: true,
            };
            if (post.category && post.category.color) {
                cssClass['post__category--' + post.category.color] = true;
            }
            return cssClass;
        },
        cssPostBlockClass(block) {
            const cssClass = {
                post__section: true,
            };
            if (block.typeHandle) {
                cssClass['post__section__' + block.typeHandle] = true;
            }
            return cssClass;
        },
    },
};
</script>
<style lang="scss">
@import '@/styles/global';

.post {
    &__image {
        position: relative;
        height: 0;
        padding-bottom: calc(56.25%);
        overflow: hidden;
        max-width: 100%;

        @include bp($laptop) {
            padding-top: 40px;
            padding-left: 40px;
            padding-right: 40px;
        }
    }

    &__container {
        @include bp($laptop) {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;

            position: relative;
            max-width: 800px;
            margin: 40px auto 40px auto;
            z-index: 10;
            background-color: #eaeaea;

            &--has-image {
                margin-top: -23%;
            }
        }
    }

    &__header {
        padding: 25px;
        background-color: #3c4146;
        color: #fff;

        @include bp($laptop) {
            width: 75%;

            background-color: #eaeaea;
            color: #000;
        }
    }

    &__author__image {
        position: relative;
        float: right;
        width: 150px;
        height: 150px;
        margin-top: -100px; // 25 + 150/2

        background-color: #666;
        border-radius: 50%;

        @include bp($laptop) {
            display: none;
        }
    }

    /*&__kicker {
        margin-bottom: 15px;
        font-size: 20px;
        letter-spacing: 0.8px;
        line-height: 1;

        @include bp($laptop) {
            text-align: center;
        }
    }*/

    &__heading {
        margin-bottom: 25px;
        font-size: 39px;
        line-height: 1.2;

        @include bp($laptop) {
            margin-top: 25px;
            font-size: 52px;
        }
    }

    &__lead {
        margin-bottom: 10px;
        font-size: 18px;
        line-height: 1.3;

        p {
            margin: 0;
        }

        @include bp($laptop) {
            font-size: 25px;
            margin-bottom: 30px;
        }
    }

    &__meta {
        display: flex;
        flex-direction: column;
        background-color: #3c4146;
        color: #fff;

        font-size: 14px;

        @include bp($laptop) {
            width: 25%;
            font-size: 16px;
        }

        &__container {
            display: flex;
            margin: 0 25px;
            padding: 15px 0;
            border-top: 1px solid #fff;

            @include bp($laptop) {
                flex-grow: 1;
                flex-direction: column;
                padding: 25px 0;
                border-top-width: 0;
            }
        }

        label {
            display: block;
            margin: 0;
            font-variant-caps: all-small-caps;
            font-weight: bold;
            line-height: 1.3;
        }
        span {
            display: block;
            line-height: 1.3;
            margin-bottom: 10px;
        }

        &__author {
            flex-grow: 1;
            @include bp($laptop) {
                flex-grow: 0;
            }

            &__image {
                display: none;
                @include bp($laptop) {
                    display: block;
                    border-radius: 50%;
                    margin-bottom: 25px;
                }
            }

            p {
                margin-top: -8px;
                line-height: 1.2;
                @include bp($laptop) {
                    font-size: 15px;
                }
            }
        }
    }

    &__category {
        padding: 4px 0 6px 0;
        font-variant-caps: all-small-caps;
        font-size: 14px;
        text-align: center;
        letter-spacing: 0.44px;
        line-height: 1;
        font-weight: bold;

        @include bp($laptop) {
            font-size: 16px;
        }
    }

    &__content {
        padding: 30px 25px;
        background-color: #fff;

        @include bp($laptop) {
            width: 75%;
            padding-top: 0;
            background-color: inherit;
        }
    }

    &__section {
        &__text {
            font-size: 15px;

            @include bp($laptop) {
                font-size: 16px;
            }

            p {
                font-size: 15px;

                @include bp($laptop) {
                    font-size: 17px;
                }
            }

            h2,
            h3 {
                font-size: 15px;
                font-weight: bold;

                @include bp($laptop) {
                    font-size: 18px;
                }
            }
        }

        &__heading {
            font-size: 15px;
            font-weight: bold;

            @include bp($laptop) {
                font-size: 18px;
            }
        }

        &__image {
            figure {
                margin: 20px 0;
                img {
                    display: block;
                    width: 100%;
                }
                figcaption {
                    margin-top: 10px;
                    font-family: $heading-font-famlily;
                    font-size: 12px;
                }
            }
        }

        &__video {
            &__container {
                width: 100%;
                position: relative;
                height: 0;
                padding-bottom: 56.25%;

                iframe {
                    display: block;
                    position: absolute;
                    left: 0;
                    top: 0;
                    height: 100%;
                    width: 100%;
                }
            }
        }
    }
}
</style>
